import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../common/alert-dialog"
import { Button } from "../common/button"
import useTranslator from "@pma/hooks/use-translator"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { RotateCcw } from "lucide-react"
import axios from "axios"
import { RESTORE_FOLDER_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"
import { Dispatch, SetStateAction, useState } from "react"

interface Props {
  folder: any
  revalidate: CallableFunction
  isMenuView?: boolean
  refetchAllFolder?: () => void
  setActiveFolder: Dispatch<SetStateAction<any>>
  setFolderItems: Dispatch<SetStateAction<{ [id: number]: any[] }>>
}

const RestoreFolder = ({
  folder,
  isMenuView = false,
  refetchAllFolder = () => {},
  revalidate,
  setActiveFolder,
  setFolderItems,
}: Props) => {
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const [open, setOpen] = useState(false)

  const restoreFolder = async () => {
    const response = await axios.post(
      SERVER_URL + RESTORE_FOLDER_ENDPOINT,
      {
        project_id: folder?.project_id,
        folders_id: [folder?.id],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const restoreFolderMutation = useMutation({
    mutationKey: ["restore-folder", folder?.id],
    mutationFn: restoreFolder,
    onSuccess: (data) => {
      setActiveFolder((pre: any) => ({ ...pre, deleted: false }))
      setFolderItems((pre: any) => {
        const recordList = pre[folder.parent_id]
        const updatedList = recordList.map((item: any) => {
          if (item.id == folder.id) return { ...item, deleted: false }
          return item
        })

        return { ...pre, [folder.parent_id]: updatedList }
      })
      toast({
        title: translate("Success"),
        description: `${folder?.name} ${translate("has been restored")}`,
        variant: "success",
      })
      setOpen(false)
      revalidate()
      refetchAllFolder()
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      setOpen(false)
      toast({
        title: translate("Error"),
        description: `${translate("Unable to restore")} ${folder?.name}`,
        variant: "error",
      })
    },
  })

  if (folder?.isRoot) {
    // Prevent deletion of root folder
    return <></>
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger onClick={() => setOpen(true)}>
        {isMenuView ? (
          <div className='flex items-center gap-2'>
            <Button size={"icon"} className='h-6 w-7' variant={"ghost"}>
              <RotateCcw size={16} />
            </Button>
            <span className='text-sm'>{`${translate("Restore folder")}`}</span>
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger>
              <Button variant='ghost' size={"icon"} className='bg-slate-200/50'>
                <RotateCcw size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{translate("Restore folder")}</TooltipContent>
          </Tooltip>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to restore")} {folder?.name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This will restore the folder")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>
            {translate("Cancel")}
          </AlertDialogCancel>
          <Button
            disabled={restoreFolderMutation?.isPending}
            loading={restoreFolderMutation?.isPending}
            onClick={() => restoreFolderMutation.mutate()}
          >
            {translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default RestoreFolder
